// pdfReports.js — Constructor de reportes PDF de BanaPrecios
//
// Expone primitivas de dibujo (header band, footer, cover, KPI cards, sections,
// disclaimer, finalize) y 4 builders de alto nivel:
//   - buildDetailedReport       (multi-página: mensual + semanal + rango)
//   - buildExecutiveReport      (1 página: KPIs + chart + insights)
//   - buildComparativeReport    (Norte vs Sur lado a lado)
//   - buildMonthlyReport        (newsletter mensual con MoM)

import jsPDF from 'jspdf';

const PRECIO_OFICIAL = 7.50;

export const PDF_COLORS = {
  navy:      [28, 33, 54],
  green:     [23, 198, 113],
  blue:      [41, 98, 255],
  yellow:    [245, 166, 35],
  red:       [245, 61, 107],
  midGray:   [107, 114, 133],
  lightGray: [155, 165, 188],
  border:    [232, 234, 240],
  softBg:    [248, 249, 252],
  white:     [255, 255, 255],
};

export const PDF_LAYOUT = {
  margin: 40,
  headerH: 56,
  footerH: 32,
  contentTop: 86,
};

const X_LEFT  = 52;
const X_MID   = 320;
const X_RIGHT = 543;

const setFill = (pdf, c) => pdf.setFillColor(c[0], c[1], c[2]);
const setText = (pdf, c) => pdf.setTextColor(c[0], c[1], c[2]);
const setDraw = (pdf, c) => pdf.setDrawColor(c[0], c[1], c[2]);

export const formatCurrency = (n, digits = 2) =>
  `$${parseFloat(n).toFixed(digits)}`;

const formatPct = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;

const buildReportId = () => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `BP-${ymd}-${rand}`;
};

const daysBetween = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
};

const SPANISH_MONTHS = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
const titleCase = (s) => (s ? s[0].toUpperCase() + s.slice(1) : '');

// ── Logo + chart capture ─────────────────────────────────────
export const getLogoBase64 = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = '/images/LogoBanaprecios.png';
  });

export const svgToDataUrl = (containerEl) =>
  new Promise((resolve) => {
    const svgEl = containerEl?.querySelector('svg');
    if (!svgEl) { resolve(null); return; }
    const { width, height } = svgEl.getBoundingClientRect();
    if (!width || !height) { resolve(null); return; }

    const clone = svgEl.cloneNode(true);
    clone.setAttribute('width', width);
    clone.setAttribute('height', height);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    const svgStr = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width, height });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

// ── Drawing primitives ───────────────────────────────────────
const drawHeaderBand = (pdf, { logoDataUrl, reportId, subtitle }) => {
  const W = pdf.internal.pageSize.getWidth();
  const { headerH } = PDF_LAYOUT;

  setFill(pdf, PDF_COLORS.navy);
  pdf.rect(0, 0, W, headerH, 'F');
  setFill(pdf, PDF_COLORS.green);
  pdf.rect(0, headerH, W, 2, 'F');

  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', 24, 10, 36, 36);
  }

  setText(pdf, PDF_COLORS.white);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text('BanaPrecios.com', 70, 26);
  setText(pdf, PDF_COLORS.lightGray);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.text(subtitle || 'Guía Oficial de Precios del Banano · Ecuador', 70, 40);

  setText(pdf, PDF_COLORS.lightGray);
  pdf.setFontSize(8);
  pdf.text(`Reporte ${reportId}`, W - 24, 22, { align: 'right' });
  pdf.text(
    `Generado: ${new Date().toLocaleDateString('es-EC')} ${new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}`,
    W - 24, 36, { align: 'right' }
  );
};

const drawFooter = (pdf, pageNum, totalPages) => {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const yLine = H - 28;

  setDraw(pdf, PDF_COLORS.border);
  pdf.setLineWidth(0.5);
  pdf.line(PDF_LAYOUT.margin, yLine, W - PDF_LAYOUT.margin, yLine);

  setText(pdf, PDF_COLORS.midGray);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.text('BanaPrecios.com · Guía de Precios', PDF_LAYOUT.margin, H - 14);
  pdf.text('banaprecios.com', W / 2, H - 14, { align: 'center' });
  pdf.text(`Página ${pageNum} de ${totalPages}`, W - PDF_LAYOUT.margin, H - 14, { align: 'right' });
};

const drawCover = (pdf, { title, subtitle, startDate, endDate, y, accentColor = PDF_COLORS.green }) => {
  const W = pdf.internal.pageSize.getWidth();

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  setText(pdf, PDF_COLORS.navy);
  pdf.text(title, PDF_LAYOUT.margin, y);
  y += 8;

  setFill(pdf, accentColor);
  pdf.rect(PDF_LAYOUT.margin, y, 48, 3, 'F');
  y += 18;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  setText(pdf, PDF_COLORS.midGray);

  if (subtitle) {
    pdf.text(subtitle, PDF_LAYOUT.margin, y);
    y += 14;
  }
  if (startDate && endDate) {
    const days = daysBetween(startDate, endDate);
    pdf.text(`Período: ${startDate} → ${endDate}  ·  ${days} días`, PDF_LAYOUT.margin, y);
    y += 14;
  }
  y += 8;

  setDraw(pdf, PDF_COLORS.border);
  pdf.setLineWidth(0.5);
  pdf.line(PDF_LAYOUT.margin, y, W - PDF_LAYOUT.margin, y);
  y += 18;

  return y;
};

const drawKpiCards = (pdf, y, kpis) => {
  const W = pdf.internal.pageSize.getWidth();
  const innerW = W - PDF_LAYOUT.margin * 2;
  const gap = 10;
  const cardW = (innerW - gap * (kpis.length - 1)) / kpis.length;
  const cardH = 64;

  kpis.forEach((kpi, i) => {
    const x = PDF_LAYOUT.margin + i * (cardW + gap);
    setFill(pdf, PDF_COLORS.softBg);
    setDraw(pdf, PDF_COLORS.border);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(x, y, cardW, cardH, 6, 6, 'FD');
    setFill(pdf, kpi.accent || PDF_COLORS.green);
    pdf.roundedRect(x, y, cardW, 4, 6, 6, 'F');
    pdf.rect(x, y + 2, cardW, 2, 'F');
    setText(pdf, PDF_COLORS.midGray);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text(kpi.label.toUpperCase(), x + 12, y + 20);
    setText(pdf, PDF_COLORS.navy);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(kpi.value, x + 12, y + 42);
    if (kpi.sub) {
      setText(pdf, kpi.subColor || PDF_COLORS.midGray);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text(kpi.sub, x + 12, y + 56);
    }
  });
  return y + cardH + 22;
};

const computeKpisFromValues = (values, label = 'caja') => {
  if (!values?.length) return null;
  const nums = values.map(v => parseFloat(v)).filter(n => !Number.isNaN(n));
  if (!nums.length) return null;
  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const diffPct = ((avg - PRECIO_OFICIAL) / PRECIO_OFICIAL) * 100;
  return {
    avg, min, max, diffPct,
    cards: [
      { label: `Promedio / ${label}`, value: formatCurrency(avg), accent: PDF_COLORS.green },
      { label: `Mínimo / ${label}`,   value: formatCurrency(min), accent: PDF_COLORS.blue },
      { label: `Máximo / ${label}`,   value: formatCurrency(max), accent: PDF_COLORS.yellow },
      {
        label: 'vs Precio Oficial',
        value: `${diffPct >= 0 ? '+' : ''}${diffPct.toFixed(1)}%`,
        sub: `Oficial ${formatCurrency(PRECIO_OFICIAL)}`,
        accent: diffPct >= 0 ? PDF_COLORS.green : PDF_COLORS.red,
        subColor: diffPct >= 0 ? PDF_COLORS.green : PDF_COLORS.red,
      },
    ],
  };
};

const addSection = async (pdf, { title, accent, chartRef, tableHeaders, tableRows, y }) => {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const bottomLimit = H - PDF_LAYOUT.footerH - 12;

  const checkPage = (needed) => {
    if (y + needed > bottomLimit) { pdf.addPage(); y = PDF_LAYOUT.contentTop; }
    return y;
  };
  checkPage(40);

  setFill(pdf, accent || PDF_COLORS.green);
  pdf.rect(PDF_LAYOUT.margin, y - 12, 4, 18, 'F');
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  setText(pdf, PDF_COLORS.navy);
  pdf.text(title, PDF_LAYOUT.margin + 12, y);
  y += 10;
  setDraw(pdf, PDF_COLORS.border);
  pdf.setLineWidth(0.5);
  pdf.line(PDF_LAYOUT.margin, y, W - PDF_LAYOUT.margin, y);
  y += 16;

  if (chartRef?.current) {
    const chartImg = await svgToDataUrl(chartRef.current);
    if (chartImg) {
      const imgW = W - PDF_LAYOUT.margin * 2;
      const imgH = (chartImg.height / chartImg.width) * imgW;
      checkPage(imgH + 10);
      pdf.addImage(chartImg.dataUrl, 'PNG', PDF_LAYOUT.margin, y, imgW, imgH);
      y += imgH + 18;
    }
  }

  if (tableHeaders?.length) {
    checkPage(28);
    setFill(pdf, PDF_COLORS.softBg);
    pdf.rect(PDF_LAYOUT.margin, y - 10, W - PDF_LAYOUT.margin * 2, 18, 'F');
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    setText(pdf, PDF_COLORS.midGray);
    tableHeaders.forEach(h => {
      pdf.text(h.label.toUpperCase(), h.x, y, h.align ? { align: h.align } : undefined);
    });
    y += 14;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    tableRows.forEach((row, idx) => {
      checkPage(16);
      if (idx % 2 === 1) {
        setFill(pdf, PDF_COLORS.softBg);
        pdf.rect(PDF_LAYOUT.margin, y - 10, W - PDF_LAYOUT.margin * 2, 14, 'F');
      }
      row.forEach(cell => {
        const c = cell.color ?? PDF_COLORS.navy;
        if (Array.isArray(c)) setText(pdf, c);
        else pdf.setTextColor(c);
        pdf.text(String(cell.value), cell.x, y, cell.align ? { align: cell.align } : undefined);
      });
      y += 14;
    });
  }
  y += 18;
  return y;
};

const drawInsightList = (pdf, y, insights) => {
  const W = pdf.internal.pageSize.getWidth();
  setFill(pdf, PDF_COLORS.softBg);
  setDraw(pdf, PDF_COLORS.border);
  pdf.setLineWidth(0.5);
  const blockH = 14 + insights.length * 16 + 10;
  pdf.roundedRect(PDF_LAYOUT.margin, y, W - PDF_LAYOUT.margin * 2, blockH, 6, 6, 'FD');

  setText(pdf, PDF_COLORS.midGray);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.text('OBSERVACIONES', PDF_LAYOUT.margin + 12, y + 16);

  setText(pdf, PDF_COLORS.navy);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  insights.forEach((line, i) => {
    pdf.text(`•  ${line}`, PDF_LAYOUT.margin + 12, y + 32 + i * 16, { maxWidth: W - PDF_LAYOUT.margin * 2 - 24 });
  });
  return y + blockH + 14;
};

const drawDualKpiCards = (pdf, y, { norte, sur }) => {
  const W = pdf.internal.pageSize.getWidth();
  const innerW = W - PDF_LAYOUT.margin * 2;
  const gap = 12;
  const cardW = (innerW - gap) / 2;
  const cardH = 92;

  const drawZoneCard = (x, label, accent, data) => {
    setFill(pdf, PDF_COLORS.softBg);
    setDraw(pdf, PDF_COLORS.border);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(x, y, cardW, cardH, 8, 8, 'FD');
    setFill(pdf, accent);
    pdf.roundedRect(x, y, 5, cardH, 8, 8, 'F');
    pdf.rect(x, y, 5, cardH - 6, 'F');

    setText(pdf, PDF_COLORS.midGray);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text(label.toUpperCase(), x + 18, y + 18);

    if (!data) {
      setText(pdf, PDF_COLORS.lightGray);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.text('Sin datos', x + 18, y + 50);
      return;
    }

    setText(pdf, PDF_COLORS.navy);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(22);
    pdf.text(formatCurrency(data.avg), x + 18, y + 48);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    setText(pdf, PDF_COLORS.midGray);
    pdf.text('promedio · caja', x + 18, y + 60);

    pdf.setFontSize(9);
    setText(pdf, PDF_COLORS.midGray);
    pdf.text(`Mín ${formatCurrency(data.min)}  ·  Máx ${formatCurrency(data.max)}`, x + 18, y + 78);
  };

  drawZoneCard(PDF_LAYOUT.margin, 'Zona Norte', PDF_COLORS.green, norte);
  drawZoneCard(PDF_LAYOUT.margin + cardW + gap, 'Zona Sur', PDF_COLORS.blue, sur);

  return y + cardH + 22;
};

const drawSpreadBox = (pdf, y, { norte, sur }) => {
  if (!norte || !sur) return y;
  const W = pdf.internal.pageSize.getWidth();
  const spread = norte.avg - sur.avg;
  const absS = Math.abs(spread);
  const winner = spread > 0 ? 'Norte' : 'Sur';
  const looser = spread > 0 ? 'Sur' : 'Norte';
  const accent = spread > 0 ? PDF_COLORS.green : PDF_COLORS.blue;

  setFill(pdf, accent);
  pdf.roundedRect(PDF_LAYOUT.margin, y, W - PDF_LAYOUT.margin * 2, 36, 8, 8, 'F');

  setText(pdf, PDF_COLORS.white);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  if (absS < 0.05) {
    pdf.text('Las dos zonas pagan prácticamente igual en este período.', PDF_LAYOUT.margin + 16, y + 22);
  } else {
    pdf.text(
      `Spread: ${formatCurrency(absS)} a favor de ${winner} sobre ${looser} en promedio.`,
      PDF_LAYOUT.margin + 16, y + 22
    );
  }
  return y + 50;
};

const drawDisclaimer = (pdf, y) => {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const bottomLimit = H - PDF_LAYOUT.footerH - 12;
  if (y + 60 > bottomLimit) { pdf.addPage(); y = PDF_LAYOUT.contentTop; }

  setDraw(pdf, PDF_COLORS.border);
  pdf.setLineWidth(0.5);
  pdf.line(PDF_LAYOUT.margin, y, W - PDF_LAYOUT.margin, y);
  y += 14;

  setText(pdf, PDF_COLORS.midGray);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.text('AVISO', PDF_LAYOUT.margin, y);
  y += 10;

  pdf.setFont('helvetica', 'normal');
  setText(pdf, PDF_COLORS.lightGray);
  const txt =
    'Los precios mostrados son referenciales, basados en reportes de la comunidad de productores y exportadores de banano del Ecuador. ' +
    'No constituyen asesoría comercial ni precio oficial vinculante. Para decisiones de compra/venta consulte con su comercializador. ' +
    'Fuente: BanaPrecios.com';
  const lines = pdf.splitTextToSize(txt, W - PDF_LAYOUT.margin * 2);
  pdf.text(lines, PDF_LAYOUT.margin, y);
  y += lines.length * 10;
  return y;
};

const finalizePdf = (pdf, { logoDataUrl, reportId, subtitle }) => {
  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    drawHeaderBand(pdf, { logoDataUrl, reportId, subtitle });
    drawFooter(pdf, i, total);
  }
};

// ── Table configs ────────────────────────────────────────────
const monthlyTableConfig = (m) => ({
  headers: [
    { label: 'Métrica', x: X_LEFT },
    { label: 'Valor',   x: X_RIGHT, align: 'right' },
  ],
  rows: [
    [{ value: 'Precio Promedio por Caja', x: X_LEFT }, { value: formatCurrency(m.avg_price_per_box), x: X_RIGHT, align: 'right' }],
    [{ value: 'Precio Promedio por Kg',   x: X_LEFT }, { value: formatCurrency(m.avg_price_per_kg, 3), x: X_RIGHT, align: 'right' }],
    [{ value: 'Precio Máximo por Caja',   x: X_LEFT }, { value: formatCurrency(m.max_price_per_box), x: X_RIGHT, align: 'right', color: PDF_COLORS.green }],
    [{ value: 'Precio Mínimo por Caja',   x: X_LEFT }, { value: formatCurrency(m.min_price_per_box), x: X_RIGHT, align: 'right', color: PDF_COLORS.red }],
  ],
});

const dailyTableConfig = (data) => ({
  headers: [
    { label: 'Fecha',       x: X_LEFT },
    { label: 'Precio/Caja', x: X_MID,   align: 'right' },
    { label: 'Precio/Kg',   x: X_RIGHT, align: 'right' },
  ],
  rows: [...data]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(item => ([
      { value: new Date(item.date).toLocaleDateString('es-EC'), x: X_LEFT },
      { value: formatCurrency(item.price_per_box),    x: X_MID,   align: 'right' },
      { value: formatCurrency(item.price_per_kg, 3),  x: X_RIGHT, align: 'right' },
    ])),
});

// Comparative table: date | norte | sur | spread
const comparativeTableConfig = (norteData, surData) => {
  const map = new Map();
  for (const d of norteData) {
    const iso = String(d.date).slice(0, 10);
    if (!map.has(iso)) map.set(iso, { iso });
    map.get(iso).norte = parseFloat(d.price_per_box);
  }
  for (const d of surData) {
    const iso = String(d.date).slice(0, 10);
    if (!map.has(iso)) map.set(iso, { iso });
    map.get(iso).sur = parseFloat(d.price_per_box);
  }
  const sorted = [...map.values()].sort((a, b) => a.iso.localeCompare(b.iso));
  // Aggregate per day (in case of multiple reports)
  const COL_NORTE = 280;
  const COL_SUR   = 410;
  const COL_SPREAD = X_RIGHT;
  return {
    headers: [
      { label: 'Fecha',  x: X_LEFT },
      { label: 'Norte',  x: COL_NORTE,  align: 'right' },
      { label: 'Sur',    x: COL_SUR,    align: 'right' },
      { label: 'Spread', x: COL_SPREAD, align: 'right' },
    ],
    rows: sorted.map(d => {
      const spread = (d.norte != null && d.sur != null) ? d.norte - d.sur : null;
      const spreadStr = spread == null ? '—' : `${spread >= 0 ? '+' : ''}${formatCurrency(spread)}`;
      const spreadColor = spread == null ? PDF_COLORS.midGray : (spread >= 0 ? PDF_COLORS.green : PDF_COLORS.red);
      return [
        { value: new Date(d.iso).toLocaleDateString('es-EC'), x: X_LEFT },
        { value: d.norte != null ? formatCurrency(d.norte) : '—', x: COL_NORTE, align: 'right', color: PDF_COLORS.green },
        { value: d.sur   != null ? formatCurrency(d.sur)   : '—', x: COL_SUR,   align: 'right', color: PDF_COLORS.blue  },
        { value: spreadStr, x: COL_SPREAD, align: 'right', color: spreadColor },
      ];
    }),
  };
};

// ── makePdfBase: inicializa PDF con metadata y portada ───────
export const makePdfBase = async ({ title, subtitle, startDate, endDate, accentColor }) => {
  const pdf = new jsPDF('p', 'pt', 'a4');
  const reportId = buildReportId();
  const logoDataUrl = await getLogoBase64();

  pdf.setProperties({
    title: `${title} · BanaPrecios`,
    subject: subtitle || 'Guía Oficial de Precios del Banano',
    author: 'BanaPrecios.com',
    keywords: 'banano, precios, ecuador, banaprecios',
    creator: 'BanaPrecios.com',
  });

  let y = PDF_LAYOUT.contentTop;
  y = drawCover(pdf, { title, subtitle, startDate, endDate, y, accentColor });

  return { pdf, y, ctx: { logoDataUrl, reportId, subtitle } };
};

// Aggregate price reports into daily averages with ISO date keys
const aggregateByDay = (priceData) => {
  const buckets = new Map();
  for (const d of priceData || []) {
    const iso = String(d.date).slice(0, 10);
    const v = parseFloat(d.price_per_box);
    if (Number.isNaN(v)) continue;
    if (!buckets.has(iso)) buckets.set(iso, []);
    buckets.get(iso).push(v);
  }
  return [...buckets.entries()]
    .map(([iso, values]) => ({
      iso,
      value: values.reduce((s, v) => s + v, 0) / values.length,
      count: values.length,
    }))
    .sort((a, b) => a.iso.localeCompare(b.iso));
};

// Produce auto-insights from period data
const buildInsightsFromData = ({ monthlyData, weeklyData, priceRangeData }) => {
  const insights = [];
  const allValues = priceRangeData?.length
    ? priceRangeData.map(d => parseFloat(d.price_per_box))
    : (weeklyData?.map(d => parseFloat(d.price_per_box)) || []);

  if (allValues.length) {
    const above = allValues.filter(v => v >= PRECIO_OFICIAL).length;
    const total = allValues.length;
    const pctAbove = (above / total) * 100;
    insights.push(`${above} de ${total} reportes (${pctAbove.toFixed(0)}%) estuvieron sobre el precio oficial de ${formatCurrency(PRECIO_OFICIAL)}.`);

    const max = Math.max(...allValues);
    const min = Math.min(...allValues);
    insights.push(`Volatilidad del período: ${formatCurrency(max - min)} entre el precio más alto y el más bajo.`);
  }

  if (monthlyData) {
    const avg = parseFloat(monthlyData.avg_price_per_box);
    const diff = avg - PRECIO_OFICIAL;
    const pct = (diff / PRECIO_OFICIAL) * 100;
    if (diff >= 0.30) insights.push(`Buen mes: el promedio quedó ${formatCurrency(diff)} (${pct.toFixed(1)}%) sobre el precio oficial.`);
    else if (diff <= -0.30) insights.push(`Mes flojo: el promedio quedó ${formatCurrency(Math.abs(diff))} bajo el precio oficial.`);
    else insights.push(`El promedio del mes quedó cerca del precio oficial (diferencia menor a $0.30).`);
  }

  // Streak detection on daily aggregates
  if (priceRangeData?.length) {
    const daily = aggregateByDay(priceRangeData);
    let streak = 0;
    for (let i = daily.length - 1; i >= 0; i--) {
      if (daily[i].value >= PRECIO_OFICIAL) streak++;
      else break;
    }
    if (streak >= 3) insights.push(`${streak} días seguidos sobre el precio oficial al cierre del período.`);
  }
  return insights;
};

// ── REPORT BUILDERS ──────────────────────────────────────────

// 1) Detallado — multi-página (mensual + semanal + rango)
export const buildDetailedReport = async ({
  startDate, endDate, monthlyData, weeklyData, priceRangeData,
  monthlyChartRef, weeklyChartRef, rangeChartRef, zoneLabel,
}) => {
  const { pdf, y: y0, ctx } = await makePdfBase({
    title: 'Reporte Completo de Precios',
    subtitle: `${zoneLabel ? `Zona ${zoneLabel} · ` : ''}Guía Oficial de Precios del Banano · Ecuador`,
    startDate, endDate,
  });
  let y = y0;

  const sourceValues = monthlyData
    ? [parseFloat(monthlyData.avg_price_per_box), parseFloat(monthlyData.min_price_per_box), parseFloat(monthlyData.max_price_per_box)]
    : (priceRangeData?.length ? priceRangeData : weeklyData || []).map(d => d.price_per_box);
  const k = computeKpisFromValues(sourceValues);
  if (k) y = drawKpiCards(pdf, y, k.cards);

  if (monthlyData) {
    const { headers, rows } = monthlyTableConfig(monthlyData);
    y = await addSection(pdf, {
      title: `Resumen Mensual — ${monthlyData.month} ${monthlyData.year}`,
      accent: PDF_COLORS.blue, chartRef: monthlyChartRef, tableHeaders: headers, tableRows: rows, y,
    });
  }
  if (weeklyData?.length) {
    const { headers, rows } = dailyTableConfig(weeklyData);
    y = await addSection(pdf, {
      title: 'Resumen Semanal', accent: PDF_COLORS.green, chartRef: weeklyChartRef,
      tableHeaders: headers, tableRows: rows, y,
    });
  }
  if (priceRangeData?.length) {
    const { headers, rows } = dailyTableConfig(priceRangeData);
    y = await addSection(pdf, {
      title: 'Histórico por Fecha', accent: PDF_COLORS.yellow, chartRef: rangeChartRef,
      tableHeaders: headers, tableRows: rows, y,
    });
  }

  drawDisclaimer(pdf, y);
  finalizePdf(pdf, ctx);
  pdf.save('banaprecios_reporte_detallado.pdf');
};

// 2) Ejecutivo — 1 página: KPIs + chart principal + insights
export const buildExecutiveReport = async ({
  startDate, endDate, monthlyData, weeklyData, priceRangeData,
  rangeChartRef, zoneLabel,
}) => {
  const { pdf, y: y0, ctx } = await makePdfBase({
    title: 'Reporte Ejecutivo',
    subtitle: `${zoneLabel ? `Zona ${zoneLabel} · ` : ''}Resumen condensado para toma de decisiones`,
    startDate, endDate,
    accentColor: PDF_COLORS.blue,
  });
  let y = y0;

  const sourceValues = monthlyData
    ? [parseFloat(monthlyData.avg_price_per_box), parseFloat(monthlyData.min_price_per_box), parseFloat(monthlyData.max_price_per_box)]
    : (priceRangeData?.length ? priceRangeData : weeklyData || []).map(d => d.price_per_box);
  const k = computeKpisFromValues(sourceValues);
  if (k) y = drawKpiCards(pdf, y, k.cards);

  // Chart principal: usa rangeChartRef si está disponible
  if (rangeChartRef?.current) {
    const { headers, rows } = dailyTableConfig((priceRangeData || []).slice(-12)); // top 12 días por brevedad
    y = await addSection(pdf, {
      title: 'Tendencia del Período', accent: PDF_COLORS.blue, chartRef: rangeChartRef,
      tableHeaders: rows.length ? headers : null, tableRows: rows.length ? rows : null, y,
    });
  }

  // Insights
  const insights = buildInsightsFromData({ monthlyData, weeklyData, priceRangeData });
  if (insights.length) y = drawInsightList(pdf, y, insights);

  drawDisclaimer(pdf, y);
  finalizePdf(pdf, ctx);
  pdf.save('banaprecios_reporte_ejecutivo.pdf');
};

// 3) Comparativo Norte vs Sur
export const buildComparativeReport = async ({
  startDate, endDate,
  norteRangeData, surRangeData,
  norteRangeChartRef, surRangeChartRef,
}) => {
  const { pdf, y: y0, ctx } = await makePdfBase({
    title: 'Reporte Comparativo · Norte vs Sur',
    subtitle: 'Análisis de spread entre las dos zonas productoras',
    startDate, endDate,
    accentColor: PDF_COLORS.blue,
  });
  let y = y0;

  const kN = computeKpisFromValues((norteRangeData || []).map(d => d.price_per_box));
  const kS = computeKpisFromValues((surRangeData   || []).map(d => d.price_per_box));

  // Dual KPI cards
  y = drawDualKpiCards(pdf, y, { norte: kN, sur: kS });

  // Spread box
  if (kN && kS) y = drawSpreadBox(pdf, y, { norte: kN, sur: kS });

  // Chart Norte
  if (norteRangeChartRef?.current) {
    y = await addSection(pdf, {
      title: 'Zona Norte — Histórico', accent: PDF_COLORS.green, chartRef: norteRangeChartRef,
      tableHeaders: null, tableRows: null, y,
    });
  }
  // Chart Sur
  if (surRangeChartRef?.current) {
    y = await addSection(pdf, {
      title: 'Zona Sur — Histórico', accent: PDF_COLORS.blue, chartRef: surRangeChartRef,
      tableHeaders: null, tableRows: null, y,
    });
  }

  // Comparative table
  if ((norteRangeData?.length || 0) > 0 || (surRangeData?.length || 0) > 0) {
    const { headers, rows } = comparativeTableConfig(norteRangeData || [], surRangeData || []);
    y = await addSection(pdf, {
      title: 'Día a día', accent: PDF_COLORS.yellow,
      chartRef: null, tableHeaders: headers, tableRows: rows, y,
    });
  }

  drawDisclaimer(pdf, y);
  finalizePdf(pdf, ctx);
  pdf.save('banaprecios_reporte_comparativo.pdf');
};

// 4) Boletín Mensual — newsletter style con MoM
export const buildMonthlyReport = async ({
  startDate, endDate, monthlyData, prevMonthlyData, weeklyData, priceRangeData,
  rangeChartRef, weeklyChartRef, zoneLabel,
}) => {
  const monthLabel = monthlyData?.month
    ? `${titleCase(monthlyData.month)} ${monthlyData.year}`
    : 'Boletín del Mes';

  const { pdf, y: y0, ctx } = await makePdfBase({
    title: `Boletín Mensual · ${monthLabel}`,
    subtitle: `${zoneLabel ? `Zona ${zoneLabel} · ` : ''}Resumen del mes con comparación vs mes anterior`,
    startDate, endDate,
    accentColor: PDF_COLORS.green,
  });
  let y = y0;

  // Hero KPIs del mes actual
  if (monthlyData) {
    const cards = [
      { label: 'Promedio Mensual', value: formatCurrency(monthlyData.avg_price_per_box), accent: PDF_COLORS.green },
      { label: 'Mínimo del Mes',   value: formatCurrency(monthlyData.min_price_per_box), accent: PDF_COLORS.blue },
      { label: 'Máximo del Mes',   value: formatCurrency(monthlyData.max_price_per_box), accent: PDF_COLORS.yellow },
    ];

    // vs Mes Anterior si existe
    if (prevMonthlyData?.avg_price_per_box) {
      const cur = parseFloat(monthlyData.avg_price_per_box);
      const prev = parseFloat(prevMonthlyData.avg_price_per_box);
      const pct = prev !== 0 ? ((cur - prev) / prev) * 100 : 0;
      cards.push({
        label: 'vs Mes Anterior',
        value: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`,
        sub: `${prevMonthlyData.month || ''} ${formatCurrency(prev)}`,
        accent: pct >= 0 ? PDF_COLORS.green : PDF_COLORS.red,
        subColor: pct >= 0 ? PDF_COLORS.green : PDF_COLORS.red,
      });
    } else {
      const k = computeKpisFromValues([parseFloat(monthlyData.avg_price_per_box)]);
      cards.push(k.cards[3]); // vs Oficial
    }

    y = drawKpiCards(pdf, y, cards);
  }

  // Mejor / peor semana del mes
  if (weeklyData?.length) {
    const weekly = aggregateByDay(weeklyData);
    if (weekly.length) {
      const best = weekly.reduce((a, b) => (b.value > a.value ? b : a));
      const worst = weekly.reduce((a, b) => (b.value < a.value ? b : a));
      const insightLines = [
        `Mejor día del mes: ${new Date(best.iso).toLocaleDateString('es-EC')} — ${formatCurrency(best.value)}.`,
        `Día más bajo: ${new Date(worst.iso).toLocaleDateString('es-EC')} — ${formatCurrency(worst.value)}.`,
      ];
      const all = buildInsightsFromData({ monthlyData, weeklyData, priceRangeData });
      y = drawInsightList(pdf, y, [...insightLines, ...all]);
    }
  }

  // Chart de tendencia del mes
  if (rangeChartRef?.current && priceRangeData?.length) {
    y = await addSection(pdf, {
      title: 'Tendencia del Mes', accent: PDF_COLORS.green, chartRef: rangeChartRef,
      tableHeaders: null, tableRows: null, y,
    });
  } else if (weeklyChartRef?.current) {
    y = await addSection(pdf, {
      title: 'Resumen Semanal del Mes', accent: PDF_COLORS.green, chartRef: weeklyChartRef,
      tableHeaders: null, tableRows: null, y,
    });
  }

  // Tabla detallada del mes (si hay range data)
  if (priceRangeData?.length) {
    const { headers, rows } = dailyTableConfig(priceRangeData);
    y = await addSection(pdf, {
      title: 'Detalle Día por Día', accent: PDF_COLORS.yellow,
      chartRef: null, tableHeaders: headers, tableRows: rows, y,
    });
  }

  drawDisclaimer(pdf, y);
  finalizePdf(pdf, ctx);
  pdf.save(`banaprecios_boletin_${(monthlyData?.month || 'mes').toLowerCase()}_${monthlyData?.year || ''}.pdf`);
};
